<template>
  <div v-if="show" class="circular-time-picker-backdrop" @click.self="cancel">
    <div ref="dialog" class="circular-time-picker-modal body-font" role="dialog" aria-modal="true" tabindex="-1" :aria-labelledby="dialogTitleId">
      <!-- Modal Header -->
      <div class="picker-header">
        <h5 :id="dialogTitleId" class="picker-title">{{ title || $t('sendReport.selectTime') || 'เลือกเวลา' }}</h5>
        <button type="button" class="close-btn" :aria-label="$t('common.close')" @click="cancel">✕</button>
      </div>

      <!-- Time Display Screen -->
      <div class="time-display-box">
        <div class="time-segments">
          <button
            type="button"
            class="time-segment-btn"
            :class="{ active: currentTab === 'hour' }"
            @click="currentTab = 'hour'"
          >
            {{ String(tempHour).padStart(2, '0') }}
          </button>
          <span class="time-colon">:</span>
          <button
            type="button"
            class="time-segment-btn"
            :class="{ active: currentTab === 'minute' }"
            @click="currentTab = 'minute'"
          >
            {{ String(tempMinute).padStart(2, '0') }}
          </button>
        </div>
        <div class="time-hint">
          <span v-if="currentTab === 'hour'">{{ isEn ? 'Select Hour' : 'เลือกชั่วโมง' }}</span>
          <span v-else>{{ isEn ? 'Select Minute' : 'เลือกนาที' }}</span>
        </div>
      </div>

      <!-- Main Content Body: Circular Clock Dial Face -->
      <div class="picker-body">
        <div class="dial-container">
          <!-- Hour / Minute Mode Switcher -->
          <div class="dial-mode-toggle mb-3">
            <button
              type="button"
              class="btn btn-xs btn-mode"
              :class="{ active: currentTab === 'hour' }"
              @click="currentTab = 'hour'"
            >
              {{ isEn ? 'Hour' : 'ชั่วโมง' }}
            </button>
            <button
              type="button"
              class="btn btn-xs btn-mode"
              :class="{ active: currentTab === 'minute' }"
              @click="currentTab = 'minute'"
            >
              {{ isEn ? 'Minute' : 'นาที' }}
            </button>
          </div>

          <!-- Circular Clock Dial Surface (256px Dual-Ring Dial) -->
          <div class="clock-dial-surface">
            <!-- Center Pivot Point -->
            <div class="clock-center-dot"></div>

            <!-- Rotating Clock Hand Pointer -->
            <div
              class="clock-hand"
              :style="{
                transform: `rotate(${handAngle}deg)`,
                height: handLength
              }"
            >
              <div class="clock-hand-head"></div>
            </div>

            <!-- Hour Numbers Ring: Universal 1-12 Clock Face -->
            <template v-if="currentTab === 'hour'">
              <button
                v-for="h in clockHours"
                :key="`hour-${h}`"
                type="button"
                class="clock-number outer-number"
                :class="{
                  active: (tempHour % 12 || 12) === h,
                  disabled: isDialHourDisabled(h)
                }"
                :style="getNumberStyle(h, 12, 100)"
                :disabled="isDialHourDisabled(h)"
                :title="getDialHourTitle(h)"
                @click="selectDialHour(h)"
              >
                {{ h }}
              </button>
            </template>

            <!-- Minute Numbers Ring (00 - 55) -->
            <template v-else-if="currentTab === 'minute'">
              <button
                v-for="m in minuteOptions"
                :key="`min-${m}`"
                type="button"
                class="clock-number outer-number"
                :class="{ active: tempMinute === m, disabled: isMinuteDisabled(m) }"
                :style="getNumberStyle(m === 0 ? 12 : m / 5, 12, 100)"
                :disabled="isMinuteDisabled(m)"
                @click="selectMinute(m)"
              >
                {{ String(m).padStart(2, '0') }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Modal Footer Action Buttons -->
      <div class="picker-footer">
        <button type="button" class="btn btn-outline-secondary font-weight-bold" @click="cancel">
          {{ isEn ? 'Cancel' : 'ยกเลิก' }}
        </button>
        <button type="button" class="btn btn-primary font-weight-bold px-4 text-white" @click="confirm">
          {{ isEn ? 'Confirm' : 'ยืนยัน' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { registerDialog } from '@/utils/dialogAccessibility';
export default {
  name: 'CircularTimePickerModal',
  props: {
    show: { type: Boolean, default: false },
    value: { type: String, default: '08:00' },
    title: { type: String, default: '' },
    minTime: { type: String, default: '08:00' },
    maxTime: { type: String, default: '16:00' },
    disabledStartMinutes: { type: Number, default: null }
  },
  data() {
    return {
      dialogTitleId: 'time-picker-title-' + this._uid,
      tempHour: 8,
      tempMinute: 0,
      currentTab: 'hour', // 'hour' | 'minute'
      outerHours: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      innerHours: [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0],
      minuteOptions: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
    }
  },
  computed: {
    isEn() {
      return this.$store?.state?.locale?.lang === 'en'
    },
    minMinutes() {
      if (!this.minTime || typeof this.minTime !== 'string') return 8 * 60
      const parts = this.minTime.split(':').map(Number)
      if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return 8 * 60
      return parts[0] * 60 + parts[1]
    },
    maxMinutes() {
      if (!this.maxTime || typeof this.maxTime !== 'string') return 16 * 60
      const parts = this.maxTime.split(':').map(Number)
      if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return 16 * 60
      return parts[0] * 60 + parts[1]
    },
    formattedTempTime() {
      const hh = String(this.tempHour).padStart(2, '0')
      const mm = String(this.tempMinute).padStart(2, '0')
      return `${hh}:${mm}`
    },
    clockHours() {
      return [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
    },
    handAngle() {
      if (this.currentTab === 'hour') {
        const h = this.tempHour % 12
        return h * 30
      } else {
        return (this.tempMinute / 60) * 360
      }
    },
    handLength() {
      return '100px'
    },
    visibleHours() {
      const minH = Math.floor(this.minMinutes / 60)
      const maxH = Math.floor(this.maxMinutes / 60)
      const start = Math.min(8, minH)
      const end = Math.max(16, maxH)
      const hours = []
      for (let h = start; h <= end; h++) {
        hours.push(h)
      }
      return hours
    },
    visibleOuterHours() {
      return this.visibleHours.filter(h => h <= 12)
    },
    visibleInnerHours() {
      return this.visibleHours.filter(h => h > 12)
    },
    visibleMinutes() {
      return this.minuteOptions.filter(m => !this.isMinuteDisabled(m))
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          this._closeRequested = false;
          this.parseValue(this.value)
          this.currentTab = 'hour'
          this.$nextTick(this.activateDialog)
        } else this.releaseDialog()
      }
    }
  },
  beforeDestroy() { this.releaseDialog() },
  deactivated() { this.releaseDialog() },
  activated() { if (this.show) this.activateDialog() },
  methods: {
    activateDialog() {
      if (!this.show || this._dialog || this._isBeingDestroyed || this._isDestroyed) return;
      this._dialog = registerDialog(this.$refs.dialog, {
        layers: () => [this.$el],
        initialFocus: () => this.$refs.dialog.querySelector('.time-segment-btn'),
        close: this.cancel,
      });
    },
    releaseDialog() {
      if (this._dialog) this._dialog.release();
      this._dialog = null;
    },
    parseValue(val) {
      let h = 8
      let m = 0

      if (val && typeof val === 'string') {
        const parts = val.split(':').map(Number)
        if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          h = Math.min(23, Math.max(0, parts[0]))
          m = Math.min(59, Math.max(0, parts[1]))
        }
      }

      if (this.isHourDisabled(h)) {
        const minH = Math.floor(this.minMinutes / 60)
        const maxH = Math.floor(this.maxMinutes / 60)
        if (h < minH) {
          h = minH
        } else if (h > maxH) {
          h = maxH
        } else {
          const validH = this.visibleHours.find(item => !this.isHourDisabled(item))
          if (validH !== undefined) h = validH
        }
      }

      this.tempHour = h

      if (this.isMinuteDisabled(m)) {
        const validM = this.minuteOptions.find(opt => !this.isMinuteDisabled(opt))
        this.tempMinute = validM !== undefined ? validM : 0
      } else {
        this.tempMinute = m
      }
    },
    getActualHourForDial(dialNum) {
      const amHour = dialNum === 12 ? 0 : dialNum
      const pmHour = dialNum === 12 ? 12 : dialNum + 12

      const amValid = !this.isHourDisabled(amHour)
      const pmValid = !this.isHourDisabled(pmHour)

      if (amValid && !pmValid) return amHour
      if (!amValid && pmValid) return pmHour
      if (amValid && pmValid) {
        return this.tempHour >= 12 ? pmHour : amHour
      }
      return amHour
    },
    isDialHourDisabled(dialNum) {
      const actualH = this.getActualHourForDial(dialNum)
      return this.isHourDisabled(actualH)
    },
    getDialHourTitle(dialNum) {
      const actualH = this.getActualHourForDial(dialNum)
      return `${String(actualH).padStart(2, '0')}:00`
    },
    selectDialHour(dialNum) {
      const actualH = this.getActualHourForDial(dialNum)
      this.selectHour(actualH)
    },
    getNumberStyle(indexVal, total, radius) {
      const angle = (indexVal * (360 / total) - 90) * (Math.PI / 180)
      const x = radius * Math.cos(angle)
      const y = radius * Math.sin(angle)
      return {
        transform: `translate(${x}px, ${y}px)`
      }
    },
    isHourDisabled(h) {
      const startOfHour = h * 60
      const endOfHour = h * 60 + 59

      if (endOfHour < this.minMinutes) {
        return true
      }
      if (startOfHour > this.maxMinutes) {
        return true
      }
      if (this.disabledStartMinutes !== null && this.disabledStartMinutes !== undefined) {
        if (endOfHour <= this.disabledStartMinutes) {
          return true
        }
      }
      return false
    },
    isMinuteDisabled(m) {
      const totalMin = (this.tempHour * 60) + m

      if (totalMin < this.minMinutes) {
        return true
      }
      if (totalMin > this.maxMinutes) {
        return true
      }
      if (this.disabledStartMinutes !== null && this.disabledStartMinutes !== undefined) {
        if (totalMin <= this.disabledStartMinutes) {
          return true
        }
      }
      return false
    },
    selectHour(h, autoAdvance = true) {
      if (this.isHourDisabled(h)) return
      this.tempHour = h

      if (this.isMinuteDisabled(this.tempMinute)) {
        const validM = this.minuteOptions.find(opt => !this.isMinuteDisabled(opt))
        this.tempMinute = validM !== undefined ? validM : 0
      }

      if (autoAdvance) {
        this.currentTab = 'minute'
      }
    },
    selectMinute(m, autoAdvance = false) {
      if (this.isMinuteDisabled(m)) return
      this.tempMinute = m
      if (autoAdvance) {
        this.confirm()
      }
    },
    cancel() {
      if (this._closeRequested || !this._dialog?.isTop()) return;
      this._closeRequested = true;
      this.$emit('update:show', false)
      this.$emit('cancel')
    },
    confirm() {
      if (this._closeRequested || !this._dialog?.isTop()) return;
      this._closeRequested = true;
      const selected = this.formattedTempTime
      this.$emit('input', selected)
      this.$emit('select', selected)
      this.$emit('update:show', false)
    }
  }
}
</script>

<style scoped>
.circular-time-picker-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  z-index: 2050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.circular-time-picker-modal {
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-width: 360px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  animation: pickerPop 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes pickerPop {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f1f5f9;
}

.picker-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.2rem;
  color: #64748b;
  cursor: pointer;
}

.time-display-box {
  background: linear-gradient(135deg, #1e40af, #2563eb);
  color: #ffffff;
  text-align: center;
  padding: 18px 16px 14px;
}

.time-segments {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.time-segment-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid transparent;
  border-radius: 12px;
  color: #ffffff;
  font-size: 2.4rem;
  font-weight: 800;
  line-height: 1;
  padding: 6px 16px;
  cursor: pointer;
  transition: all 0.18s ease;
}

.time-segment-btn.active {
  background: rgba(255, 255, 255, 0.32);
  border-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.time-colon {
  font-size: 2.2rem;
  font-weight: 800;
  opacity: 0.9;
}

.time-hint {
  margin-top: 8px;
  font-size: 0.85rem;
  opacity: 0.92;
  font-weight: 600;
}

.picker-body {
  padding: 18px 16px;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dial-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.dial-mode-toggle {
  display: flex;
  gap: 8px;
}

.btn-mode {
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 20px;
  padding: 4px 16px;
  font-size: 0.85rem;
  font-weight: 700;
}

.btn-mode.active {
  background: #1e40af;
  color: #ffffff;
}

.clock-dial-surface {
  position: relative;
  width: 256px;
  height: 256px;
  border-radius: 50%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.04);
}

.clock-center-dot {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #1e40af;
  border-radius: 50%;
  z-index: 10;
}

.clock-hand {
  position: absolute;
  bottom: 50%;
  left: calc(50% - 1.5px);
  width: 3px;
  background: #1e40af;
  transform-origin: bottom center;
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), height 0.2s ease;
  z-index: 8;
  pointer-events: none;
}

.clock-hand-head {
  position: absolute;
  top: -16px;
  left: calc(50% - 16px);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(30, 64, 175, 0.25);
  border: 2px solid #1e40af;
}

.clock-number {
  position: absolute;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: transparent;
  border: none;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9;
  transition: all 0.15s ease;
}

.inner-number {
  width: 32px;
  height: 32px;
  font-size: 0.82rem;
  color: #334155;
  font-weight: 700;
  background: rgba(241, 245, 249, 0.85);
}

.clock-number.active {
  background: #1e40af !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.4);
}

.clock-number.disabled {
  opacity: 0.28;
  cursor: not-allowed;
  pointer-events: none;
  background: transparent !important;
  color: #94a3b8 !important;
}

.picker-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 16px;
  border-top: 1px solid #f1f5f9;
  background: #ffffff;
}
</style>
